import type {
  CreateHouseAccountCustomerRequest,
  HouseAccountCustomerView,
  HouseAccountCustomersPage,
  HouseAccountSettlementMethod,
  HouseAccountReportView,
  UpdateHouseAccountCustomerRequest,
} from 'shared-models/browser';
import { authenticatedInstance } from '../index/index.service';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type { HouseAccountCustomerView, HouseAccountReportView } from 'shared-models/browser';

const sessionHeaders = (employeeSession: string) => ({ 'x-employee-session': employeeSession });

class HouseAccountsService {
  async list(
    tenantId: string,
    employeeSession: string,
    page: number,
    pageSize: number,
    search = '',
    status?: 'active' | 'blocked',
    excludeSystem = false,
  ): Promise<HouseAccountCustomersPage> {
    try {
      const response = await authenticatedInstance.get<HouseAccountCustomersPage>(`/house-accounts/tenant/${tenantId}`, {
        headers: sessionHeaders(employeeSession),
        params: {
          page, pageSize, ...(search ? { search } : {}), ...(status ? { status } : {}), ...(excludeSystem ? { excludeSystem: 'true' } : {}),
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading House Accounts'));
    }
  }

  async report(
    tenantId: string,
    customerId: number,
    employeeSession: string,
    range?: { from: string; to: string },
  ): Promise<HouseAccountReportView> {
    try {
      const response = await authenticatedInstance.get<HouseAccountReportView>(
        `/house-accounts/tenant/${tenantId}/${customerId}/report`, { headers: sessionHeaders(employeeSession), params: range },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading House Account report'));
    }
  }

  async create(tenantId: string, payload: CreateHouseAccountCustomerRequest, employeeSession: string): Promise<HouseAccountCustomerView> {
    try {
      const response = await authenticatedInstance.post<HouseAccountCustomerView>(
        `/house-accounts/tenant/${tenantId}`, payload, { headers: sessionHeaders(employeeSession) },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed creating House Account'));
    }
  }

  async update(tenantId: string, customerId: number, payload: UpdateHouseAccountCustomerRequest, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch(`/house-accounts/tenant/${tenantId}/${customerId}`, payload, { headers: sessionHeaders(employeeSession) });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating House Account'));
    }
  }

  async configureCard(
    tenantId: string,
    customerId: number,
    payload: { cardNumber: string; expDateMMYY: string; cvv: string },
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.post(`/house-accounts/tenant/${tenantId}/${customerId}/card`, payload, { headers: sessionHeaders(employeeSession) });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed configuring House Account card'));
    }
  }

  async settle(
    tenantId: string,
    customerId: number,
    method: HouseAccountSettlementMethod,
    amount: number,
    employeeSession: string,
    cashDrawerPrinterId?: number,
  ): Promise<void> {
    try {
      await authenticatedInstance.post(
        `/house-accounts/tenant/${tenantId}/${customerId}/settlements/${method}`,
        { amount, ...(method === 'cash' && cashDrawerPrinterId != null ? { cashDrawerPrinterId } : {}) },
        { headers: sessionHeaders(employeeSession) },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed settling House Account debt'));
    }
  }

  async assignToOrder(tenantId: string, orderId: number, customerId: number | null, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch(
        `/house-accounts/tenant/${tenantId}/order/${orderId}/customer`,
        { customerId },
        { headers: sessionHeaders(employeeSession) },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed assigning House Account'));
    }
  }
}

export const houseAccountsService = new HouseAccountsService();